import type { ActorDatabaseHandle } from "@-/dev.improve.haniverse.database.actor";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import {
  scheduleReview,
  type ReviewRating,
} from "@-/dev.improve.haniverse.lib.cards.user-card.fsrs";
import { getUserCardStage } from "@-/dev.improve.haniverse.lib.cards.user-card.mastery";
import { eq } from "@-/com.npmjs.drizzle-orm";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import { uuidv4 } from "@-/com.npmjs.uuid";
import { hanForRating, readReviewBonusHan } from "@-/dev.improve.haniverse.lib.han-currency";
import { readReviewRechargeChange } from "#use-review-recharge-query";

type ScheduledReview = ReturnType<typeof scheduleReview>;

/**
 * The committed review: the advanced schedule, plus the id of the `user_card_review` row that
 * records it. That id is what `undoStudyCardReview` needs to roll this exact review back, so it
 * is returned rather than kept internal — re-deriving it later would mean guessing which of a
 * card's reviews was the one just written.
 */
export type PersistedStudyCardReview = {
  /**
   * The review row named by the caller's `userCardReviewId` already existed, so NOTHING was
   * written: this call is a retry of an operation that had in fact landed. `familiarGrapheme` is
   * `null` in that case (the crossing, if any, was reported by the write that landed) and
   * `nextCard` is the card row as it stands now.
   */
  alreadyPersisted: boolean;
  /** A character's meaning card crossed Familiar; pronunciation and word reviews never qualify. */
  familiarGrapheme: string | null;
  nextCard: ScheduledReview["nextCard"];
  userCardReviewId: string;
};

/**
 * Atomically records one completed review and advances its FSRS card.
 *
 * The FSRS schedule is computed INSIDE the transaction, from the fsrs_card row
 * as committed — never from a render-time snapshot. A cached snapshot can lag
 * the database (the UI re-serving a just-reviewed card is exactly when it
 * does), and scheduling from it silently rewinds the card: a second review
 * computed from the pre-first-review state overwrites reps/stability as if the
 * first review never happened. Reading the row in the same transaction that
 * writes it makes consecutive reviews always compound, regardless of what any
 * cache believed.
 *
 * Navigation must only happen after this resolves. Otherwise the next-card
 * preload can observe the old due date and cache the card that was just
 * reviewed, causing it to reappear one card later.
 */
/** One card's review within a batch. */
export type StudyCardReviewInput = {
  fsrsCardId: string;
  /**
   * Caller-chosen id for the `fsrs_card_review_log` row. Paired with `userCardReviewId`; see
   * there. Generated when omitted.
   */
  fsrsCardReviewLogId?: string;
  rating: ReviewRating;
  studyCardId: string;
  /**
   * Caller-chosen id for the `user_card_review` row, which makes the write DURABLY IDEMPOTENT:
   * a second call with the same id finds the row already there and writes nothing, reporting
   * `alreadyPersisted`. This is what lets a rating whose outcome was lost mid-flight (a crash, a
   * cancelled await, a retry after a timeout) be re-submitted without double-advancing FSRS —
   * an in-memory latch cannot know whether the earlier attempt landed, but the row can.
   * Generated when omitted, in which case every call writes.
   */
  userCardReviewId?: string;
};

/**
 * Record one review against an already-open transaction.
 *
 * Split out so the single- and multi-card entry points share ONE implementation of the FSRS advance
 * — including the read-inside-the-transaction rule the module comment above depends on. Taking `tx`
 * rather than a database handle is what lets a batch run every review in a single transaction.
 */
async function persistOneReview(
  tx: Parameters<Parameters<ActorDatabaseHandle["transaction"]>[0]>[0],
  {
    fsrsCardId,
    fsrsCardReviewLogId: providedReviewLogId,
    now,
    rating,
    reviewBonusHan,
    studyCardId,
    userCardReviewId: providedUserCardReviewId,
  }: StudyCardReviewInput & {
    now: Date;
    /** Today's per-review bonus from the daily 红包 (`readReviewBonusHan`), usually 0. */
    reviewBonusHan: number;
  },
): Promise<PersistedStudyCardReview> {
  const nowIso = now.toISOString();
  const reviewLogId = providedReviewLogId ?? uuidv4();
  const userCardReviewId = providedUserCardReviewId ?? uuidv4();

  const fsrsCard = await tx.query.fsrsCard.findFirst({
    where: { id: { eq: fsrsCardId } },
  });
  if (fsrsCard === undefined) {
    throw new Error(`Expected an FSRS card to advance for review, found none (${fsrsCardId})`);
  }

  // Durable deduplication, read INSIDE the same transaction that would write: a caller-named
  // review row that already exists proves this operation landed earlier. Read through the raw
  // handle so an undone (soft-deleted) review still counts as landed — replaying it would
  // resurrect a rating the user explicitly took back.
  if (providedUserCardReviewId !== undefined) {
    const existingReview = await tx.queryRaw.userCardReview.findFirst({
      columns: { id: true },
      where: { id: { eq: providedUserCardReviewId } },
    });
    if (existingReview !== undefined) {
      return {
        alreadyPersisted: true,
        familiarGrapheme: null,
        nextCard: fsrsCard,
        userCardReviewId: providedUserCardReviewId,
      };
    }
  }

  const { nextCard, reviewLog } = scheduleReview(fsrsCard, rating, now);
  // Read inside the transaction, before this review's row exists: the balance it leaves is
  // stored on the row, so undoing the review restores the balance before it exactly.
  const reviewRecharge = await readReviewRechargeChange(tx, rating);

  await tx
    .insert(actorSchema.fsrsCardReviewLog)
    .values({
      createdAt: nowIso,
      deletedAt: null,
      id: reviewLogId,
      ...reviewLog,
    })
    .run();

  await tx
    .insert(actorSchema.userCardReview)
    .values({
      cardId: studyCardId,
      createdAt: nowIso,
      deletedAt: null,
      fsrsCardReviewLogId: reviewLogId,
      hanEarned: hanForRating(rating) + reviewBonusHan,
      id: userCardReviewId,
      reviewedAt: nowIso,
      reviewRechargeAfter: reviewRecharge.after,
    })
    .run();

  const updateResult = await tx
    .update(actorSchema.fsrsCard)
    .set(nextCard)
    .where(eq(actorSchema.fsrsCard.id, fsrsCardId))
    .run();

  if (updateResult.changes !== 1) {
    throw new Error(
      `Expected to advance exactly one FSRS card for review, updated ${updateResult.changes}`,
    );
  }

  let familiarGrapheme: string | null = null;
  if (
    getUserCardStage({ fsrsCard, manuallyMasteredAt: null }) === "new" &&
    getUserCardStage({ fsrsCard: nextCard, manuallyMasteredAt: null }) !== "new"
  ) {
    const card = await tx.query.userCard.findFirst({
      where: { id: { eq: studyCardId } },
      with: { userGraphemeMeaningCard: true },
    });
    if (card?.manuallyMasteredAt === null) {
      familiarGrapheme = card.userGraphemeMeaningCard?.grapheme ?? null;
    }
  }
  return {
    alreadyPersisted: false,
    familiarGrapheme,
    nextCard,
    userCardReviewId,
  };
}

/**
 * Atomically record SEVERAL completed reviews and advance their FSRS cards.
 *
 * The sentence-production drills rate every word of a sentence in one action, which is a genuinely
 * different shape from the one-card-per-screen review loop. Doing it as N calls to
 * {@link persistStudyCardReview} would open N transactions, and each transaction's post-commit
 * flush is what `queueCoalescedInvalidations` collapses into a single refetch pass — so N
 * transactions means N passes. That cost is already known to be the dominant one here: the
 * post-rating stall was measured growing ~95ms per reviewed card. One transaction keeps it to one
 * pass regardless of how many words the sentence had.
 *
 * Each review still reads its `fsrs_card` row from inside the shared transaction, so consecutive
 * reviews of the SAME card (which a sentence can contain, if a word repeats) still compound
 * correctly rather than both scheduling from the pre-batch state.
 *
 * The whole batch rolls back together: a sentence is rated as one act, and a half-applied batch
 * would leave the user with some words advanced and others not, with nothing to indicate which.
 */
export async function persistStudyCardReviewBatch({
  actorDb,
  now,
  reviews,
}: {
  actorDb: ActorDatabaseHandle;
  now: Date;
  reviews: readonly StudyCardReviewInput[];
}): Promise<PersistedStudyCardReview[]> {
  if (hasLength(reviews, 0)) {
    return [];
  }
  return actorDb.transaction(async (tx) => {
    // Every review in the batch shares `now`, so they share its local day's bonus.
    const reviewBonusHan = await readReviewBonusHan(tx, now);
    const results: PersistedStudyCardReview[] = [];
    for (const review of reviews) {
      results.push(await persistOneReview(tx, { ...review, now, reviewBonusHan }));
    }
    return results;
  });
}

/**
 * Record one review inside the caller's transaction, for a write that must land together with it:
 * a quiz's first answer enrolls the card AND reviews it, and neither may commit without the other.
 */
export async function persistStudyCardReviewInTransaction(
  tx: Parameters<Parameters<ActorDatabaseHandle["transaction"]>[0]>[0],
  { now, ...review }: StudyCardReviewInput & { now: Date },
): Promise<PersistedStudyCardReview> {
  return persistOneReview(tx, { ...review, now, reviewBonusHan: await readReviewBonusHan(tx, now) });
}

export async function persistStudyCardReview({
  actorDb,
  fsrsCardId,
  fsrsCardReviewLogId,
  now,
  rating,
  studyCardId,
  userCardReviewId,
}: StudyCardReviewInput & {
  actorDb: ActorDatabaseHandle;
  now: Date;
}): Promise<PersistedStudyCardReview> {
  const [result] = await persistStudyCardReviewBatch({
    actorDb,
    now,
    reviews: [{ fsrsCardId, fsrsCardReviewLogId, rating, studyCardId, userCardReviewId }],
  });
  if (result === undefined) {
    throw new Error(`Expected a scheduled review for card ${fsrsCardId}, got none`);
  }
  return result;
}
